import { useState, type FormEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Modal } from '../../../components/ui/Modal'
import { Field } from '../../../components/ui/Field'
import { Input, Textarea, Select } from '../../../components/ui/Input'
import { Button } from '../../../components/ui/Button'
import { tasksApi } from '../../../api/tasks.api'
import { ApiError } from '../../../api/client'
import { toast } from '../../../store/toastStore'
import { PRIORITY_OPTIONS } from './PriorityBadge'
import { LabelPicker } from './LabelPicker'
import { AssigneePicker } from './AssigneePicker'
import { ProjectDocsPicker } from './ProjectDocsPicker'
import { AiToolToggle } from './AiToolToggle'
import { DocumentLinkPicker } from '../../../components/DocumentLinkPicker'
import type { AiTool, ProjectDocId, TaskPriority } from '../../../api/types'
import detailStyles from '../detail/TaskDetailModal.module.css'

interface TaskFormModalProps {
  projectId: string
  initialStatusId?: string
  parentTaskId?: string
  onClose: () => void
  onCreated?: (taskId: string) => void
}

export function TaskFormModal({ projectId, initialStatusId, parentTaskId, onClose, onCreated }: TaskFormModalProps) {
  const queryClient = useQueryClient()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [priority, setPriority] = useState<TaskPriority>('MEDIUM')
  const [dueDate, setDueDate] = useState('')
  const [estimatedMinutes, setEstimatedMinutes] = useState('')
  const [assigneeUserIds, setAssigneeUserIds] = useState<string[]>([])
  const [assigneeRoleIds, setAssigneeRoleIds] = useState<string[]>([])
  const [labelIds, setLabelIds] = useState<string[]>([])
  const [technicalScope, setTechnicalScope] = useState('')
  const [definitionOfDone, setDefinitionOfDone] = useState('')
  const [aiWorkInstruction, setAiWorkInstruction] = useState('')
  const [aiTool, setAiTool] = useState<AiTool | null>(null)
  const [relevantProjectDocs, setRelevantProjectDocs] = useState<ProjectDocId[]>([])
  const [documentIds, setDocumentIds] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: () =>
      tasksApi.create({
        projectId,
        title,
        description: description || undefined,
        statusId: initialStatusId,
        priority,
        dueDate: dueDate || undefined,
        estimatedMinutes: estimatedMinutes ? Number(estimatedMinutes) : undefined,
        parentTaskId,
        assigneeUserIds,
        assigneeRoleIds,
        labelIds,
        technicalScope: technicalScope || undefined,
        definitionOfDone: definitionOfDone || undefined,
        aiWorkInstruction: aiWorkInstruction || undefined,
        aiTool,
        relevantProjectDocs,
        documentIds,
      }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      toast.success('Aufgabe angelegt.')
      onCreated?.(res.data.id)
      onClose()
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Anlegen fehlgeschlagen.'),
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    mutation.mutate()
  }

  return (
    <Modal
      title={parentTaskId ? 'Unteraufgabe anlegen' : 'Neue Aufgabe'}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={onSubmit} loading={mutation.isPending}>
            Anlegen
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        {error ? <div style={{ color: 'var(--danger)', fontSize: 'var(--text-sm)' }}>{error}</div> : null}

        <Field label="Titel" htmlFor="task-title">
          <Input id="task-title" value={title} onChange={(e) => setTitle(e.target.value)} required autoFocus />
        </Field>

        <Field label="Beschreibung (optional)" htmlFor="task-description">
          <Textarea id="task-description" value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>

        <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
          <div style={{ flex: 1 }}>
            <Field label="Priorität" htmlFor="task-priority">
              <Select id="task-priority" value={priority} onChange={(e) => setPriority(e.target.value as TaskPriority)}>
                {PRIORITY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <div style={{ flex: 1 }}>
            <Field label="Fälligkeitsdatum" htmlFor="task-due">
              <Input id="task-due" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </Field>
          </div>
          <div style={{ flex: 1 }}>
            <Field label="Geschätzt (Min.)" htmlFor="task-estimate">
              <Input id="task-estimate" type="number" min={1} value={estimatedMinutes} onChange={(e) => setEstimatedMinutes(e.target.value)} />
            </Field>
          </div>
        </div>

        <Field label="Labels">
          <LabelPicker projectId={projectId} selectedIds={labelIds} onToggle={(id) => setLabelIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]))} />
        </Field>

        <Field label="Verantwortliche">
          <AssigneePicker
            userIds={assigneeUserIds}
            roleIds={assigneeRoleIds}
            onToggleUser={(id) => setAssigneeUserIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]))}
            onToggleRole={(id) => setAssigneeRoleIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]))}
          />
        </Field>

        <div className={detailStyles.section}>
          <div className={detailStyles.sectionTitle}>Technische Planung</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <Field label="Technischer Scope (optional)" htmlFor="task-technical-scope">
              <Textarea
                id="task-technical-scope"
                value={technicalScope}
                onChange={(e) => setTechnicalScope(e.target.value)}
                placeholder="Betroffene Resource, Domain, Services, Dependencies, APIs, DB-Bereiche, Security-Anforderungen…"
                style={{ minHeight: 100 }}
              />
            </Field>
            <Field label="Definition of Done (optional)" htmlFor="task-dod">
              <Textarea
                id="task-dod"
                value={definitionOfDone}
                onChange={(e) => setDefinitionOfDone(e.target.value)}
                placeholder="Wann gilt die Aufgabe als abgeschlossen?"
                style={{ minHeight: 100 }}
              />
            </Field>
          </div>
        </div>

        <div className={detailStyles.section}>
          <div className={detailStyles.sectionTitle}>KI-Entwicklung</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <Field label="KI-Arbeitsauftrag (optional)" htmlFor="task-ai-instruction" hint="Vollständiger Copy/Paste-Arbeitsauftrag für Claude Code oder Codex">
              <div style={{ marginBottom: 'var(--space-2)' }}>
                <AiToolToggle value={aiTool} onChange={setAiTool} />
              </div>
              <Textarea
                id="task-ai-instruction"
                value={aiWorkInstruction}
                onChange={(e) => setAiWorkInstruction(e.target.value)}
                placeholder="Aufgabe, Ziel, Scope, Nicht Teil der Aufgabe, technische Anforderungen, Security, Tests, Definition of Done, gewünschter Abschlussbericht…"
                style={{ minHeight: 180 }}
              />
            </Field>
            <Field label="Relevante Projektdokumente">
              <ProjectDocsPicker
                selected={relevantProjectDocs}
                onToggle={(id) => setRelevantProjectDocs((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]))}
              />
            </Field>
            <Field label="Verknüpfte Dokumente" hint="Verweist auf echte Einträge aus der Dokumentation dieses Projekts">
              <DocumentLinkPicker
                projectId={projectId}
                linkedIds={documentIds}
                onToggle={(id) => setDocumentIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]))}
              />
            </Field>
          </div>
        </div>
      </form>
    </Modal>
  )
}
